import type { JSONContent } from '@tiptap/core'
import { GitCompareArrows } from 'lucide-react'
import { createTiptapDiff } from '@/utils/tiptap-diff'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { PolicyContentViewer } from '@/components/screenata/PolicyContentViewer'
import { createAnnotatedDocuments } from './tiptapDiffAnnotators'

type TiptapSideBySideDiffViewProps = {
  previous: JSONContent | null
  current: JSONContent
  previousLabel: string
  currentLabel: string
  layout?: 'previous-first' | 'current-first'
}

export function TiptapSideBySideDiffView({
  previous,
  current,
  previousLabel,
  currentLabel,
  layout = 'previous-first',
}: TiptapSideBySideDiffViewProps) {
  if (!previous) {
    return (
      <div className='min-h-0 flex-1 overflow-x-auto'>
        <ScrollArea className='h-full min-w-[760px]'>
          <div className='grid min-h-full grid-cols-2 gap-0 divide-x divide-border'>
            {layout === 'current-first' ? (
              <>
                <VersionPane
                  label={currentLabel}
                  tone='current'
                  content={current}
                />
                <EmptyVersionPane />
              </>
            ) : (
              <>
                <EmptyVersionPane />
                <VersionPane
                  label={currentLabel}
                  tone='current'
                  content={current}
                />
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    )
  }

  const diff = createTiptapDiff(previous, current)
  const annotated = createAnnotatedDocuments(previous, current)
  const panes =
    layout === 'current-first'
      ? [
          {
            label: currentLabel,
            tone: 'current' as const,
            content: annotated.current,
          },
          {
            label: previousLabel,
            tone: 'previous' as const,
            content: annotated.previous,
          },
        ]
      : [
          {
            label: previousLabel,
            tone: 'previous' as const,
            content: annotated.previous,
          },
          {
            label: currentLabel,
            tone: 'current' as const,
            content: annotated.current,
          },
        ]

  if (!diff.hasChanges) {
    return (
      <div className='flex min-h-[180px] flex-1 items-center justify-center px-6 text-center'>
        <div>
          <GitCompareArrows className='mx-auto h-5 w-5 text-text-placeholder' />
          <p className='mt-2 text-[13px] font-medium text-text-secondary'>
            No changes between {previousLabel} and {currentLabel}.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-0 flex-1 overflow-x-auto'>
      <ScrollArea className='h-full min-w-[760px]'>
        <div className='grid min-h-full grid-cols-2 gap-0 divide-x divide-border'>
          {panes.map((pane) => (
            <VersionPane
              key={`${pane.tone}-${pane.label}`}
              label={pane.label}
              tone={pane.tone}
              content={pane.content}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

export function TiptapSideBySideLoadingView({
  previousLabel,
  currentLabel,
  layout = 'previous-first',
}: {
  previousLabel: string
  currentLabel: string
  layout?: 'previous-first' | 'current-first'
}) {
  const panes =
    layout === 'current-first'
      ? [
          { label: currentLabel, tone: 'current' as const },
          { label: previousLabel, tone: 'previous' as const },
        ]
      : [
          { label: previousLabel, tone: 'previous' as const },
          { label: currentLabel, tone: 'current' as const },
        ]

  return (
    <div className='min-h-0 flex-1 overflow-x-auto'>
      <ScrollArea className='h-full min-w-[760px]'>
        <div className='grid min-h-full grid-cols-2 gap-0 divide-x divide-border'>
          {panes.map((pane) => (
            <LoadingVersionPane
              key={`${pane.tone}-${pane.label}`}
              label={pane.label}
              tone={pane.tone}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

function VersionPane({
  label,
  tone,
  content,
}: {
  label: string
  tone: 'previous' | 'current'
  content: JSONContent
}) {
  const isPrevious = tone === 'previous'

  return (
    <div className='flex min-h-full min-w-0 flex-col bg-card'>
      <div
        className={cn(
          'shrink-0 border-b px-4 py-2 text-[12px] font-semibold',
          isPrevious
            ? 'border-status-red/30 bg-status-red-bg text-status-red-strong'
            : 'border-emerald-100 bg-emerald-50 text-emerald-800 dark:border-status-green/30 dark:bg-status-green-bg dark:text-status-green-strong'
        )}
      >
        {isPrevious ? 'Previous' : 'Current'} {label}
      </div>
      <div className='px-5 py-5 [&_.ProseMirror]:max-w-none [&_.ProseMirror]:text-[13px] [&_.ProseMirror]:leading-6 [&_.editor-container]:pt-0 [&_.editor-content]:rounded-none'>
        <PolicyContentViewer
          contentKey={`compare-${tone}-${label}`}
          plateContent={content}
          readOnly
          reviewMode={false}
          enableComments={false}
          className='h-full'
          editorClassName='mt-0 mb-0'
        />
      </div>
    </div>
  )
}

function EmptyVersionPane() {
  return (
    <div className='flex min-h-full min-w-0 flex-col bg-card'>
      <div className='shrink-0 border-b border-border bg-[#F5F6F8] px-4 py-2 text-[12px] font-semibold text-text-subtle dark:bg-surface-subtle'>
        Compare version
      </div>
      <div className='sticky top-0 flex h-[calc(95vh-220px)] min-h-[240px] items-center justify-center px-5 py-5'>
        <div className='text-center'>
          <GitCompareArrows className='mx-auto h-5 w-5 text-text-placeholder' />
          <p className='mt-2 text-[13px] font-medium text-text-secondary'>
            Please choose version
          </p>
          <p className='mt-1 text-[12px] text-text-subtle'>
            Select a version above to load the comparison.
          </p>
        </div>
      </div>
    </div>
  )
}

function LoadingVersionPane({
  label,
  tone,
}: {
  label: string
  tone: 'previous' | 'current'
}) {
  const isPrevious = tone === 'previous'

  return (
    <div className='flex min-h-full min-w-0 flex-col bg-card'>
      <div
        className={cn(
          'shrink-0 border-b px-4 py-2 text-[12px] font-semibold',
          isPrevious
            ? 'border-status-red/30 bg-status-red-bg text-status-red-strong'
            : 'border-emerald-100 bg-emerald-50 text-emerald-800 dark:border-status-green/30 dark:bg-status-green-bg dark:text-status-green-strong'
        )}
      >
        {isPrevious ? 'Previous' : 'Current'} {label}
      </div>
      <div className='px-5 py-5'>
        <div className='space-y-8'>
          <div className='space-y-4'>
            <Skeleton className='h-8 w-3/4 rounded-sm' />
            <div className='space-y-2'>
              <Skeleton className='h-4 w-2/5 rounded-sm' />
              <Skeleton className='h-4 w-1/2 rounded-sm' />
              <Skeleton className='h-4 w-5/12 rounded-sm' />
              <Skeleton className='h-4 w-7/12 rounded-sm' />
            </div>
          </div>
          <div className='space-y-3'>
            <Skeleton className='h-6 w-1/3 rounded-sm' />
            <Skeleton className='h-4 w-full rounded-sm' />
            <Skeleton className='h-4 w-11/12 rounded-sm' />
            <Skeleton className='h-4 w-10/12 rounded-sm' />
            <Skeleton className='h-4 w-3/4 rounded-sm' />
          </div>
          <div className='space-y-3'>
            <Skeleton className='h-6 w-2/5 rounded-sm' />
            <Skeleton className='h-4 w-full rounded-sm' />
            <Skeleton className='h-4 w-9/12 rounded-sm' />
            <Skeleton className='h-4 w-11/12 rounded-sm' />
          </div>
        </div>
      </div>
    </div>
  )
}
